/**
 * Pure decision logic for the MediaBrowser's infinite-scroll pagination
 * trigger. Extracted from the `handleScroll` callback (behaviour-preserving
 * — the `setPage`/`getMedia` side effects stay in the component; only the
 * WHEN-TO-FETCH predicate moves here) so the 100px-from-bottom threshold and
 * its isLoading/isNotFound guards are unit-testable without a real DOM
 * scroll container.
 *
 * Coverage-map cell: builder:11.4 (Masonry results — scroll -> next page).
 * qa-validator (fleet/w3-bld-panel, 2026-07-26) could not get a live browser
 * scroll to cross this threshold and had no positive control that page-2 CAN
 * fire at all (G-146) — this pins the threshold math + guards mechanically,
 * closing that half of the gap; the "does a real scroll event reach this
 * container" half stays a browser-only concern.
 */

/** Distance-from-bottom (px) at which the next page should start loading. */
export const SCROLL_NEAR_BOTTOM_PX = 100;

/**
 * @param {{scrollTop:number, clientHeight:number, scrollHeight:number}} metrics
 * @param {boolean} isLoading  a fetch is already in flight
 * @param {boolean} isNotFound  the current search returned zero results
 * @returns {boolean} true iff a next-page fetch should be triggered
 */
export function shouldFetchNextPage(metrics, isLoading, isNotFound) {
  const { scrollTop, clientHeight, scrollHeight } = metrics;
  const nearBottom =
    scrollTop + clientHeight + SCROLL_NEAR_BOTTOM_PX >= scrollHeight;
  return nearBottom && !isLoading && !isNotFound;
}
